import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { LogoMark } from "./Logo";
import axios from "axios";
import { 
  ArrowLeft, Plus, Trash2, Save, 
  ChevronDown
} from "lucide-react";

const InvoiceBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const [invoice, setInvoice] = useState({
    client_id: "",
    invoice_number: "",
    issue_date: new Date().toISOString().split("T")[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
    notes: "",
    tax_rate: 0,
    discount: 0,
    currency: "USD",
    status: "draft",
    taxes: [],
    country_code: ""
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [taxConfigs, setTaxConfigs] = useState({});

  useEffect(() => {
    fetchClients();
    if (isEditing) {
      fetchInvoice();
    }
    // Fetch tax configs, then auto-apply user's default country
    fetchTaxConfigsAndApplyDefaults();
  }, [id]);

  const fetchTaxConfigsAndApplyDefaults = async () => {
    try {
      const { data } = await axios.get(`${API}/tax-configs`);
      setTaxConfigs(data);
      // If creating a new invoice, apply the user's default country taxes
      if (!isEditing && user?.default_country && data[user.default_country]) {
        const config = data[user.default_country];
        const taxes = config.taxes.map(t => ({ label: t.label, rate: t.rate, amount: 0 }));
        // Also default currency based on country
        const countryCurrency = { US: "USD", UK: "GBP", EU: "EUR", DE: "EUR", FR: "EUR", IN: "INR", CA: "CAD", AU: "AUD", SG: "SGD", AE: "AED", JP: "JPY", BR: "BRL" };
        const currency = countryCurrency[user.default_country] || "USD";
        setInvoice(prev => ({ ...prev, taxes, country_code: user.default_country, tax_rate: 0, currency }));
      }
    } catch (e) {}
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/invoices/${id}`);
      setInvoice({
        client_id: data.client_id,
        invoice_number: data.invoice_number,
        issue_date: data.issue_date,
        due_date: data.due_date,
        items: data.items.length > 0 ? data.items : [{ description: "", quantity: 1, rate: 0, amount: 0 }],
        notes: data.notes || "",
        tax_rate: data.tax_rate || 0,
        discount: data.discount || 0,
        currency: data.currency || "USD",
        status: data.status,
        taxes: data.taxes || [],
        country_code: data.country_code || ""
      });
      setSelectedClient(data.client);
    } catch (err) {
      console.error("Failed to fetch invoice:", err);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setInvoice(prev => ({ ...prev, client_id: client.id }));
    setShowClientDropdown(false);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index][field] = value;
    
    if (field === "quantity" || field === "rate") {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].rate) || 0);
    }
    
    setInvoice(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (invoice.items.length > 1) {
      setInvoice(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateSubtotal = () => {
    return invoice.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateTax = () => {
    const sub = calculateSubtotal();
    if (invoice.taxes && invoice.taxes.length > 0) {
      return invoice.taxes.reduce((sum, t) => sum + (sub * (parseFloat(t.rate) || 0) / 100), 0);
    }
    return sub * (parseFloat(invoice.tax_rate) || 0) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() - (parseFloat(invoice.discount) || 0);
  };

  const handleSave = async (status = invoice.status) => {
    const errors = {};
    if (!invoice.client_id) errors.client = "Please select a client before saving.";
    if (invoice.items.every(item => !item.description)) errors.items = "Add at least one line item with a description.";
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const el = document.querySelector('[data-error="true"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setValidationErrors({});

    setSaving(true);
    try {
      const payload = {
        ...invoice,
        status,
        items: invoice.items.filter(item => item.description),
        taxes: invoice.taxes && invoice.taxes.length > 0 ? invoice.taxes.map(t => ({
          label: t.label,
          rate: parseFloat(t.rate) || 0,
          amount: calculateSubtotal() * (parseFloat(t.rate) || 0) / 100
        })) : undefined
      };

      if (isEditing) {
        await axios.put(`${API}/invoices/${id}`, payload);
      } else {
        await axios.post(`${API}/invoices`, payload);
      }
      
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to save invoice:", err);
      setValidationErrors({ save: err.response?.data?.detail || "Failed to save invoice. Please check your connection and try again." });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: invoice.currency
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-[15px] text-[#6B7280]">Loading invoice...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2.5">
              <LogoMark size={28} />
              <span className="font-semibold text-[16px] text-[#0A0A0A]">
                {isEditing ? "Edit Invoice" : "New Invoice"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="btn-outline disabled:opacity-50"
              data-testid="save-draft-btn"
            >
              Save draft
            </button>
            <button
              onClick={() => handleSave("sent")}
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-send-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save & send"}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Invoice Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <div className={`bg-white border ${validationErrors.client ? "border-red-400" : "border-[#E5E7EB]"} p-4 sm:p-6`} data-error={!!validationErrors.client}>
              <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-4">Bill to</h2>
              {validationErrors.client && (
                <p className="text-[13px] text-red-600 mb-3 flex items-center gap-1.5" data-testid="error-client">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full shrink-0" />{validationErrors.client}
                </p>
              )}
              
              <div className="relative">
                <button
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                  className="w-full input flex items-center justify-between text-left"
                  data-testid="client-select"
                >
                  {selectedClient ? (
                    <div>
                      <p className="font-semibold text-[14px] text-[#0A0A0A]">{selectedClient.name}</p>
                      <p className="text-[13px] text-[#6B7280]">{selectedClient.email}</p>
                    </div>
                  ) : (
                    <span className="text-[14px] text-[#9CA3AF]">Select a client...</span>
                  )}
                  <ChevronDown className="w-4 h-4 text-[#6B7280]" />
                </button>
                
                {showClientDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] shadow-lg z-10 max-h-64 overflow-y-auto">
                    {clients.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-[13px] text-[#6B7280] mb-2">No clients yet</p>
                        <Link to="/clients" className="text-[13px] text-[#1A3DFF] font-semibold hover:underline">
                          Add your first client
                        </Link>
                      </div>
                    ) : (
                      clients.map(client => (
                        <button
                          key={client.id}
                          onClick={() => handleClientSelect(client)}
                          className="w-full px-4 py-3 text-left hover:bg-[#F9FAFB] border-b border-[#E5E7EB] last:border-b-0 transition-colors"
                          data-testid={`client-option-${client.id}`}
                        >
                          <p className="font-semibold text-[14px] text-[#0A0A0A]">{client.name}</p>
                          <p className="text-[12px] text-[#6B7280]">{client.email}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Details */}
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6">
              <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-4">Invoice details</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Invoice number</label>
                  <input
                    type="text"
                    value={invoice.invoice_number}
                    onChange={(e) => setInvoice(prev => ({ ...prev, invoice_number: e.target.value }))}
                    placeholder="Auto-generated if blank"
                    className="input font-mono text-[14px]"
                    data-testid="invoice-number"
                  />
                  <p className="text-[11px] text-[#9CA3AF] mt-1">Leave blank to auto-generate</p>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Issue date</label>
                  <input
                    type="date"
                    value={invoice.issue_date}
                    onChange={(e) => setInvoice(prev => ({ ...prev, issue_date: e.target.value }))}
                    className="input text-[14px]"
                    data-testid="issue-date"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Due date</label>
                  <input
                    type="date"
                    value={invoice.due_date}
                    onChange={(e) => setInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                    className="input text-[14px]"
                    data-testid="due-date"
                  />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className={`bg-white border ${validationErrors.items ? "border-red-400" : "border-[#E5E7EB]"} p-4 sm:p-6`} data-error={!!validationErrors.items}>
              <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-4">Items</h2>
              {validationErrors.items && (
                <p className="text-[13px] text-red-600 mb-3 flex items-center gap-1.5" data-testid="error-items">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full shrink-0" />{validationErrors.items}
                </p>
              )}
              
              <div className="space-y-4">
                {/* Desktop Header */}
                <div className="hidden sm:grid grid-cols-12 gap-4 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide px-1">
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Items */}
                {invoice.items.map((item, index) => (
                  <div key={index} data-testid={`item-row-${index}`}>
                    {/* Desktop row */}
                    <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-5">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, "description", e.target.value)}
                          placeholder="Item description"
                          className="input text-[14px]"
                          data-testid={`item-description-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                          min="0"
                          step="1"
                          className="input text-right font-mono text-[14px]"
                          data-testid={`item-qty-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                          min="0"
                          step="0.01"
                          className="input text-right font-mono text-[14px]"
                          data-testid={`item-rate-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <p className="text-right font-mono font-semibold text-[14px] text-[#0A0A0A] px-4 py-3">
                          {formatCurrency(item.amount)}
                        </p>
                      </div>
                      <div className="col-span-1">
                        <button
                          onClick={() => removeItem(index)}
                          className="p-2 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                          disabled={invoice.items.length === 1}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Mobile stacked layout */}
                    <div className="sm:hidden bg-[#F9FAFB] border border-[#E5E7EB] p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-[#6B7280] uppercase">Item {index + 1}</span>
                        <button
                          onClick={() => removeItem(index)}
                          className="p-1 text-[#9CA3AF] hover:text-[#EF4444]"
                          disabled={invoice.items.length === 1}
                          data-testid={`remove-item-mobile-${index}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, "description", e.target.value)}
                        placeholder="Description"
                        className="input text-[14px]"
                        data-testid={`item-description-mobile-${index}`}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-[#6B7280] uppercase">Qty</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            min="0"
                            className="input text-right font-mono text-[14px]"
                            data-testid={`item-qty-mobile-${index}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-[#6B7280] uppercase">Rate</label>
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                            min="0"
                            className="input text-right font-mono text-[14px]"
                            data-testid={`item-rate-mobile-${index}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-[#6B7280] uppercase">Amount</label>
                          <p className="font-mono font-semibold text-[14px] text-[#0A0A0A] text-right py-2.5">
                            {formatCurrency(item.amount)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={addItem}
                  className="flex items-center gap-2 text-[13px] text-[#1A3DFF] font-semibold hover:underline"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add item
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6">
              <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-2">Notes</h2>
              <p className="text-[12px] text-[#9CA3AF] mb-3">Payment instructions, thank-you message, or project details. Visible on the invoice and PDF.</p>
              <textarea
                value={invoice.notes}
                onChange={(e) => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes or payment instructions..."
                rows={3}
                className="input resize-none text-[14px]"
                data-testid="invoice-notes"
              />
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Save Error */}
            {validationErrors.save && (
              <div className="bg-red-50 border border-red-200 p-4 text-[13px] text-red-700" data-testid="error-save">
                {validationErrors.save}
              </div>
            )}
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 sticky top-20 sm:top-24">
              <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-6">Summary</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-[14px] text-[#6B7280]">Subtotal</span>
                  <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{formatCurrency(calculateSubtotal())}</span>
                </div>

                {/* Country/Tax selector */}
                <div>
                  <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Tax Region</label>
                  <select
                    value={invoice.country_code || ""}
                    onChange={(e) => {
                      const code = e.target.value;
                      if (!code) {
                        setInvoice(prev => ({ ...prev, taxes: [], country_code: "", tax_rate: 0 }));
                        return;
                      }
                      const config = taxConfigs[code];
                      if (config) {
                        const taxes = config.taxes.map(t => ({ label: t.label, rate: t.rate, amount: 0 }));
                        setInvoice(prev => ({ ...prev, taxes, country_code: code, tax_rate: 0 }));
                      }
                    }}
                    className="input text-[13px] py-2"
                    data-testid="tax-country-select"
                  >
                    <option value="">Manual tax</option>
                    <option value="US">US (Sales Tax)</option>
                    <option value="UK">UK (VAT 20%)</option>
                    <option value="EU">EU (VAT 21%)</option>
                    <option value="DE">Germany (19%)</option>
                    <option value="FR">France (20%)</option>
                    <option value="IN">India (GST)</option>
                    <option value="CA">Canada (GST+PST)</option>
                    <option value="AU">Australia (10%)</option>
                    <option value="SG">Singapore (9%)</option>
                    <option value="AE">UAE (5%)</option>
                    <option value="JP">Japan (10%)</option>
                    <option value="BR">Brazil</option>
                  </select>
                </div>

                {/* Multi-tax lines */}
                {invoice.taxes && invoice.taxes.length > 0 ? (
                  <div className="space-y-2">
                    {invoice.taxes.map((t, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <input
                          type="text"
                          value={t.label}
                          onChange={(e) => {
                            const taxes = [...invoice.taxes];
                            taxes[i] = { ...taxes[i], label: e.target.value };
                            setInvoice(prev => ({ ...prev, taxes }));
                          }}
                          className="w-20 input text-[12px] py-1.5 px-2"
                          data-testid={`tax-label-${i}`}
                        />
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={t.rate}
                            onChange={(e) => {
                              const taxes = [...invoice.taxes];
                              taxes[i] = { ...taxes[i], rate: parseFloat(e.target.value) || 0 };
                              setInvoice(prev => ({ ...prev, taxes }));
                            }}
                            min="0"
                            step="0.1"
                            className="w-16 input text-right font-mono text-[12px] py-1.5 px-2"
                            data-testid={`tax-rate-${i}`}
                          />
                          <span className="text-[12px] text-[#6B7280]">%</span>
                        </div>
                        <span className="font-mono text-[12px] text-[#0A0A0A] w-16 text-right">{formatCurrency(calculateSubtotal() * (t.rate / 100))}</span>
                        <button onClick={() => {
                          const taxes = invoice.taxes.filter((_, j) => j !== i);
                          setInvoice(prev => ({ ...prev, taxes }));
                        }} className="p-0.5 text-[#9CA3AF] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => setInvoice(prev => ({ ...prev, taxes: [...prev.taxes, { label: "Tax", rate: 0, amount: 0 }] }))} className="text-[12px] text-[#1A3DFF] font-medium hover:underline" data-testid="add-tax-line">+ Add tax line</button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[14px] text-[#6B7280]">Tax (%)</span>
                      <input
                        type="number"
                        value={invoice.tax_rate}
                        onChange={(e) => setInvoice(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        step="0.1"
                        className="w-20 input text-right font-mono text-[13px] py-2 px-3"
                        data-testid="tax-rate"
                      />
                    </div>
                    {invoice.tax_rate > 0 && (
                      <div className="flex justify-between text-[13px]">
                        <span className="text-[#6B7280]">Tax amount</span>
                        <span className="font-mono text-[#0A0A0A]">{formatCurrency(calculateTax())}</span>
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[14px] text-[#6B7280]">Discount</span>
                  <input
                    type="number"
                    value={invoice.discount}
                    onChange={(e) => setInvoice(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="0.01"
                    className="w-24 input text-right font-mono text-[13px] py-2 px-3"
                    data-testid="discount"
                  />
                </div>

                <div className="border-t border-[#E5E7EB] pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-[15px] text-[#0A0A0A]">Total</span>
                    <span className="font-display text-[24px] tracking-[-0.02em] text-[#0A0A0A]">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>

                <div className="pt-4">
                  <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Currency</label>
                  <select
                    value={invoice.currency}
                    onChange={(e) => setInvoice(prev => ({ ...prev, currency: e.target.value }))}
                    className="input text-[14px]"
                    data-testid="currency-select"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="NZD">NZD - New Zealand Dollar</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                    <option value="AED">AED - UAE Dirham</option>
                    <option value="SEK">SEK - Swedish Krona</option>
                    <option value="NOK">NOK - Norwegian Krone</option>
                    <option value="DKK">DKK - Danish Krone</option>
                    <option value="ZAR">ZAR - South African Rand</option>
                    <option value="MXN">MXN - Mexican Peso</option>
                    <option value="BRL">BRL - Brazilian Real</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="HKD">HKD - Hong Kong Dollar</option>
                    <option value="PLN">PLN - Polish Zloty</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InvoiceBuilder;
